import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { Spinner } from 'react-bootstrap';
import { CHUNGCHINGOAINGU_API } from '@app/utils/apiUrl';
import { Get, Put, Post } from '@app/utils/httpProvider';
import { messageToast, status200, trangThaiGPHNValue } from '../../../../utils/define';
import { URI_INTERNAL } from '../../../../utils/pathLocations';
import ChungChiNgoaiNguForm from './ChungChiNgoaiNguForm';

const toInputDate = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const ChungChiNgoaiNguEditComponent = () => {
    const history = useHistory();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const id = searchParams.get('id');

    const [isLoading, setIsLoading] = useState(false);

    const { data: itemData, isLoading: isFetching } = useQuery(
        ['chungChiNgoaiNguDetail', id],
        async () => {
            if (!id || id === 'undefined' || id === 'null') return null;
            const apiPath = CHUNGCHINGOAINGU_API.GET_ITEM;
            const response = await Get(apiPath, id);
            return response?.data || response;
        },
        {
            enabled: !!id && id !== 'undefined' && id !== 'null',
            cacheTime: 0,
            staleTime: 0,
            refetchOnMount: true,
            onError: (err) => {
                toast.error('Không thể lấy dữ liệu chứng chỉ ngoại ngữ!');
                history.push(URI_INTERNAL.CHUNGCHINGOAINGU_PATH);
            }
        }
    );

    const editFunc = useMutation((value) => {
        const apiPath = CHUNGCHINGOAINGU_API.UPDATE_ITEM;
        if (typeof Put === 'function') {
            return Put(apiPath, id, value, false);
        } else {
            return Post(apiPath, id, value, false);
        }
    });

    const handleEdit = async (values) => {
        setIsLoading(true);
        try {
            const model = {
                id: id,
                accountId: values.accountId,
                soChungChi: values.soChungChi,
                tenChungChi: values.tenChungChi,
                loaiNgoaiNgu: values.loaiNgoaiNgu || null,
                trinhDo: values.trinhDo,
                diemSo: values.diemSo ? parseFloat(values.diemSo) : null,
                ngayCap: values.ngayCap ? new Date(values.ngayCap).toISOString() : null,
                ngayHetHan: values.ngayHetHan ? new Date(values.ngayHetHan).toISOString() : null,
                thoiHan: values.thoiHan || null,
                donViCap: values.donViCap || null,
                quocGiaCap: values.quocGiaCap,
                fileMinhChungId: values.fileMinhChungId || null,
                ghiChu: values.ghiChu || null,
                trangThai: trangThaiGPHNValue.choDuyet
            };

            editFunc.mutate(model, {
                onSuccess: (res) => {
                    if (res.status !== status200) {
                        toast.error(messageToast.messageToastUpdateError || 'Cập nhật thất bại');
                    } else {
                        toast.success(messageToast.messageToastUpdateSuccess || 'Cập nhật chứng chỉ ngoại ngữ thành công!');
                        history.push(URI_INTERNAL.CHUNGCHINGOAINGU_PATH);
                    }
                },
                onError: (err) => {
                    toast.error(err?.message || 'Đã có lỗi xảy ra, vui lòng thử lại!');
                },
                onSettled: () => {
                    setIsLoading(false);
                }
            });
        } catch (error) {
            setIsLoading(false);
            console.error('Lỗi cập nhật Chứng chỉ ngoại ngữ:', error);
            toast.error('Đã có lỗi xảy ra, vui lòng thử lại!');
        }
    };

    if (isFetching) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-3 text-muted">Đang tải dữ liệu chứng chỉ ngoại ngữ...</span>
            </div>
        );
    }

    if (!itemData && !isFetching) {
        return <div className="text-center mt-5 text-danger fw-500">Không tìm thấy dữ liệu chứng chỉ ngoại ngữ!</div>;
    }

    const initialValues = {
        accountId: itemData?.accountId || itemData?.AccountId || '',
        soChungChi: itemData?.soChungChi || itemData?.SoChungChi || '',
        tenChungChi: itemData?.tenChungChi || itemData?.TenChungChi || '',
        loaiNgoaiNgu: itemData?.loaiNgoaiNgu || itemData?.LoaiNgoaiNgu || '',
        trinhDo: itemData?.trinhDo || itemData?.TrinhDo || '',
        diemSo: itemData?.diemSo || itemData?.DiemSo || '',
        ngayCap: toInputDate(itemData?.ngayCap || itemData?.NgayCap),
        ngayHetHan: toInputDate(itemData?.ngayHetHan || itemData?.NgayHetHan),
        thoiHan: itemData?.thoiHan || itemData?.ThoiHan || '',
        donViCap: itemData?.donViCap || itemData?.DonViCap || '',
        quocGiaCap: itemData?.quocGiaCap || itemData?.QuocGiaCap || '',
        fileMinhChungId: itemData?.fileMinhChungId || itemData?.FileMinhChungId || '',
        fileMinhChungPath: itemData?.fileMinhChungPath || itemData?.FileMinhChungPath || '',
        fileMinhChungName: itemData?.fileMinhChungName || itemData?.FileMinhChungName || '',
        ghiChu: itemData?.ghiChu || itemData?.GhiChu || '',
        trangThai: itemData?.trangThai || itemData?.TrangThai || ''
    };

    return (
        <ChungChiNgoaiNguForm
            title="Cập nhật Chứng chỉ ngoại ngữ"
            submitText="Lưu thay đổi"
            initialValues={initialValues}
            onSubmit={handleEdit}
            onCancel={() => history.push(URI_INTERNAL.CHUNGCHINGOAINGU_PATH)}
            isSubmitting={isLoading}
        />
    );
};

class ChungChiNgoaiNguEdit extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, errorMessage: '' };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, errorMessage: error.toString() };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Lỗi Render tại trang Edit:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="p-4 m-4" style={{ border: '2px dashed red', borderRadius: '8px', backgroundColor: '#fff5f5' }}>
                    <h4 className="text-danger"><i className="fas fa-exclamation-triangle me-2"></i> Lỗi Code (Crash Component)</h4>
                    <p className="text-dark fw-500">Ứng dụng đã bắt được lỗi nên không bị màn hình trắng. Vui lòng xem thông báo lỗi dưới đây:</p>
                    <pre className="p-3 bg-dark text-warning rounded" style={{ whiteSpace: 'pre-wrap' }}>{this.state.errorMessage}</pre>
                </div>
            );
        }
        return <ChungChiNgoaiNguEditComponent {...this.props} />;
    }
}

export default ChungChiNgoaiNguEdit;
